import { useEffect, useRef, useState } from "react";
import { IconAtom, LabelAtom, MenuAtom } from "../../atoms";
import styles from "./styles.module.scss";

interface Option {
  label: string;
  icon: string;
  disabled: boolean;
  onClick: () => void;
}

interface Props {
  options: Option[];
  rowId: string;
}

export function ButtonWithMenuMolecule(props: Readonly<Props>) {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuItemRefs = useRef<HTMLButtonElement[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    if (menuItemRefs.current[0]) {
      menuItemRefs.current[0].focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    document.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const toggleMenu = () => {
    if (!buttonRef.current) return;

    setAnchorRect(buttonRef.current.getBoundingClientRect());
    setIsOpen((prev) => !prev);
  };

  const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleMenu();
    }
  };

  const handleMenuItemKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const items = menuItemRefs.current;
    const lastIndex = items.length - 1;

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;

      case "Tab":
        if (e.shiftKey && index === 0) {
          e.preventDefault();
          items[lastIndex]?.focus();
        } else if (!e.shiftKey && index === lastIndex) {
          e.preventDefault();
          items[0]?.focus();
        }
        break;

      default:
        break;
    }
  };

  return (
    <div className={styles.container} ref={wrapperRef}>
      <button
        ref={buttonRef}
        id={`button-acties-${props.rowId}`}
        className={styles.actionButton}
        title="Actie menu"
        onClick={(e) => {
          e.stopPropagation();
          toggleMenu();
        }}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onKeyDown={handleButtonKeyDown}
      >
        ⋮
      </button>
      <MenuAtom isVisible={isOpen} anchorRect={anchorRect}>
        {props.options.map((opt, index) => (
          <button
            ref={(el) => {
              if (el) menuItemRefs.current[index] = el;
            }}
            key={opt.label}
            id={`button-${opt.label.toLowerCase()}-${props.rowId}`}
            title={opt.label}
            onClick={(e) => {
              e.stopPropagation();
              opt.onClick();
              setIsOpen(false);
              buttonRef.current?.focus();
            }}
            disabled={opt.disabled}
            onKeyDown={(e) => {
              e.stopPropagation();
              handleMenuItemKeyDown(e, index);
            }}
          >
            <LabelAtom id={opt.label} type={"default"}>
              {opt.label}
            </LabelAtom>
            <IconAtom icon={opt.icon} size={"large"} alt={opt.label} />
          </button>
        ))}
      </MenuAtom>
    </div>
  );
}
